import { Build, Component, Element, Fragment, h, Host, Listen, Prop, State } from '@stencil/core';
import { AuthenticationStatusEnum, Link, SessionInterface, TopMenuConfigurationInterface } from '../../../interfaces';
import { TopMenuService, user } from '../../../services';

@Component({
    tag: 'web-top-menu',
    shadow: false
})
export class TopMenu {
    private configuration: TopMenuConfigurationInterface;

    @Prop() features: string;
    @Element() rootElement: HTMLWebTopMenuElement;

    @State() showMenu = false;
    @State() isLoggedIn: boolean;
    @State() userName: string;

    async componentWillLoad() {
        if (!Build.isBrowser) return;
        const session: SessionInterface = user.getSession();
        this.isLoggedIn = session?.status ? session.status === AuthenticationStatusEnum.LoggedIn : !!session;
        if (this.isLoggedIn) {
            const topMenuService = new TopMenuService();
            this.configuration = await topMenuService.getConfiguration();
            this.userName = user.getFullWrittenName() || '';
        }
    }

    /**
     * Handles menu click events.
     */
    private handleOnMenuClick = () => {
        this.showMenu = !this.showMenu;
    };

    /**
     * Handles stencil menu click events.
     * @param event The event that was triggered.
     */
    private handleOnStencilMenuClick = ({ currentTarget }: Event) => {
        const origin = (currentTarget as Element).getAttribute('aria-controls');
        const menu = document.querySelector(`#${origin}`);
        menu['open']({ status: this.isLoggedIn, userName: this.userName });
    };

    /**
     * Handles outside click events to close the menu.
     * @param event The click event.
     */
    @Listen('click', { target: 'window' })
    handleOnOutsideClick({ target }: Event) {
        if (!this.rootElement.contains(target as HTMLElement)) {
            this.showMenu = false;
        }
    }

    /**
     * Handles user state changes.
     * @param event The custom event containing the user state.
     */
    @Listen('userStateChange', { target: 'window' })
    async handleUserStateChange({
        detail: { isLoggedIn, userName }
    }: CustomEvent<{ isLoggedIn: boolean; userName: string }>) {
        if (isLoggedIn && !this.configuration) {
            this.configuration = await new TopMenuService().getConfiguration();
        }
        this.isLoggedIn = isLoggedIn;
        this.userName = userName;
    }

    /**
     * Renders the contact item.
     * @param questionLink The link to the contact page.
     * @returns The contact item.
     */
    renderContactItem = ({ linkTitle, linkText, url, target }: Link) => (
        <Fragment>
            <li class="item contact-item" role="listitem">
                <a title={linkTitle} href={url} target={target}>
                    <web-icon class="contact-icon" name="chat"></web-icon>
                    <span>{linkText}</span>
                </a>
            </li>
        </Fragment>
    );

    /**
     * Renders the personal menu item.
     * @param menuLink The link to the personal menu.
     * @param menuItems The menu items.
     * @param userName The user name.
     * @returns The personal menu item.
     */
    renderPersonalMenuItem = ({ linkTitle, linkText }: Link, menuItems: Link[], userName: string) => (
        <Fragment>
            <li class={'item personal-menu-item--new-menu'} role="listitem" onClick={this.handleOnMenuClick}>
                <a title={linkTitle} href="javascript:void(0);" class="personal-menu">
                    <span class="insurance-map">
                        <web-icon class="arrow-icon" name="arrow-turn-right-down"></web-icon>
                        <span class="insurance-text">{linkText}</span>
                    </span>
                    <web-icon class="personal-data-icon" name="personal-data"></web-icon>
                    <span class="user-name" data-notranslate>{userName}</span>
                    <web-icon class="open-menu-icon" name="chevron-filled-down"></web-icon>
                </a>
                {this.showMenu && (
                    <menu class="dropdown-menu">
                        {menuItems.map(({ linkText, url, linkTitle }) => (
                            <li>
                                <a title={linkTitle} href={url}>
                                    {linkText}
                                </a>
                            </li>
                        ))}
                    </menu>
                )}
            </li>
        </Fragment>
    );

    /**
     * Renders the account menu item.
     * @returns The account menu item.
     */
    renderAccountMenuItem = () => (
        <li role="listitem">
            <web-button
                id="account-menu-button"
                aria-controls="account-menu"
                aria-expanded="false"
                class="mobile-menu-button mobile-menu-button-account"
                type="secondary"
                size="medium"
                icon-start="logged-in-state"
                onClick={this.handleOnStencilMenuClick}
            ></web-button>
        </li>
    );

    /**
     * Renders the mobile menu item.
     * @returns The mobile menu item.
     */
    renderMobileMenuItem = () => (
        <li>
            <web-button
                id="mobile-menu-button"
                aria-controls="mobile-menu"
                aria-expanded="false"
                class="mobile-menu-button mobile-menu-button-stencil"
                type="secondary"
                size="small"
                icon-start="menu"
                onClick={this.handleOnStencilMenuClick}
            ></web-button>
        </li>
    );

    render() {
        /*
        TODO: The line below is what you would like here, but stencil cannot do that yet @4.15.
        return <Fragment>{this.isLoggedIn ? <slot name="logged-in"></slot> : <slot name="anonymous"></slot>}</Fragment>;
        */

        return (
            <Host>
                <div class={{ content: true, hidden: this.isLoggedIn }}>
                    <slot name="anonymous"></slot>
                </div>
                {this.isLoggedIn && (
                    <div class={{ content: true, hidden: !this.isLoggedIn }}>
                        <ul class="auth-menu" role="list">
                            {this.renderAccountMenuItem()}
                            {this.renderPersonalMenuItem(
                                this.configuration.menuLink,
                                this.configuration.menuItems,
                                this.userName
                            )}
                            {this.renderContactItem(this.configuration.questionLink)}

                            {this.renderMobileMenuItem()}
                        </ul>
                    </div>
                )}
            </Host>
        );
    }
}
